import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SchoolSelect, isMaster, schoolName } from "../components/SchoolSelect";

export default function Students() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", roll_no: "", class_name: "", section: "", parent_email: "", school_id: "" });

  const load = async () => { const { data } = await api.get("/students"); setRows(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.parent_email) delete payload.parent_email;
      if (!master) delete payload.school_id;
      await api.post("/students", payload);
      toast.success("Student added");
      setOpen(false);
      setForm({ name: "", roll_no: "", class_name: "", section: "", parent_email: "", school_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove student?")) return;
    try { await api.delete(`/students/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canManage = ["admin", "principal"].includes(me.role);
  const master = isMaster(me);

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Students</h1>
          <p className="text-zinc-500 mt-1">Class rosters and student records.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-student-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" /> Add Student</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Student</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Roll No</Label><Input required value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })} /></div>
                  <div><Label>Class</Label><Input required value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="10" /></div>
                  <div><Label>Section</Label><Input required value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="A" /></div>
                </div>
                <div><Label>Parent Email (optional)</Label><Input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} /></div>
                {master && <SchoolSelect value={form.school_id} onChange={(v) => setForm({ ...form, school_id: v })} testId="student-school-select" />}
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="student-submit-btn">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Roll</TableHead>
              <TableHead>Class</TableHead><TableHead>Section</TableHead>
              <TableHead>Parent Email</TableHead>
              {canManage && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} className="row-hover">
                <TableCell className="font-medium">{s.name}{master && <div className="text-xs font-normal text-indigo-700">{schoolName(s.school_id)}</div>}</TableCell>
                <TableCell>{s.roll_no}</TableCell>
                <TableCell>{s.class_name}</TableCell>
                <TableCell>{s.section}</TableCell>
                <TableCell className="text-zinc-500">{s.parent_email || "—"}</TableCell>
                {canManage && (
                  <TableCell>
                    <button onClick={() => remove(s.id)} className="text-zinc-400 hover:text-rose-600 transition-colors"><Trash size={16} /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">No students yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
